import { useEffect, useMemo } from 'react'
import { usePage } from '@inertiajs/react'
import type { SharedProps, NavigableWorker } from '@/frontend/types/inertia'

// Legacy localStorage key kept only for older callers without user/org context.
export const SELECTED_WORKER_STORAGE_KEY = 'wfn-selected-worker'

export interface SelectedWorkerStorageContext {
  userId: number | string | null | undefined
  organizationId: string | null | undefined
}

export function selectedWorkerStorageContextFromProps(props: SharedProps): SelectedWorkerStorageContext {
  return {
    userId: props.auth.user?.id,
    organizationId: props.auth.user?.active_organization?.id,
  }
}

function selectedWorkerStorageKey(context?: SelectedWorkerStorageContext): string {
  if (!context?.userId || !context.organizationId) return SELECTED_WORKER_STORAGE_KEY

  return `${SELECTED_WORKER_STORAGE_KEY}:${context.userId}:${context.organizationId}`
}

export function persistSelectedWorker(id: string, context?: SelectedWorkerStorageContext) {
  const key = selectedWorkerStorageKey(context)
  let changed = true
  try {
    changed = localStorage.getItem(key) !== id
    localStorage.setItem(key, id)
    if (key !== SELECTED_WORKER_STORAGE_KEY) localStorage.removeItem(SELECTED_WORKER_STORAGE_KEY)
  } catch {
    // Storage may be unavailable (private mode/quota); selection just won't persist.
  }
  if (changed) syncSelectionToServer(id)
}

// Fire-and-forget: a falha só custa o endereçamento cross-device.
function syncSelectionToServer(id: string) {
  const csrf = document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content
  if (!csrf) return

  void fetch('/dashboard/digital_worker_selection', {
    method: 'PATCH',
    credentials: 'same-origin',
    keepalive: true,
    headers: {
      'Content-Type': 'application/json',
      'X-CSRF-Token': csrf,
    },
    body: JSON.stringify({ digital_worker_id: id }),
  }).catch(() => {})
}

function readStoredWorkerId(context?: SelectedWorkerStorageContext): string | null {
  const key = selectedWorkerStorageKey(context)
  try {
    return localStorage.getItem(key)
  } catch {
    return null
  }
}

/**
 * Resolves the currently selected agent for the worker-first sidebar.
 * Priority: worker show URL > digital_worker_id query param > server-side
 * selection (cross-device, SZ-1581) > scoped localStorage cache.
 * Only workers present in navigableResources (current org) count as selected.
 */
export function useSelectedWorker(): NavigableWorker | null {
  const { props, url } = usePage<SharedProps>()
  const workers = props.navigableResources?.workers ?? []
  const serverSelectedId = props.lastDigitalWorkerId ?? null
  const storageContext = useMemo(
    () => selectedWorkerStorageContextFromProps(props),
    [props.auth.user?.id, props.auth.user?.active_organization?.id],
  )

  const selected = useMemo(() => {
    const showMatch = url.match(/^\/dashboard\/digital_workers\/([^/?#]+)/)
    const urlId = showMatch && showMatch[1] !== 'new' ? decodeURIComponent(showMatch[1]) : null

    const queryString = url.split('?')[1] ?? ''
    const queryId = new URLSearchParams(queryString).get('digital_worker_id')

    const candidateIds = [urlId, queryId, serverSelectedId, readStoredWorkerId(storageContext)]
    for (const id of candidateIds) {
      if (!id) continue
      const worker = workers.find((w) => w.id === id)
      if (worker) return worker
    }
    return null
  }, [url, workers, serverSelectedId, storageContext])

  useEffect(() => {
    if (selected) persistSelectedWorker(selected.id, storageContext)
  }, [selected, storageContext])

  return selected
}
